/** Preserve the player's choice while the client swaps between the hub and a
 *  floor. This is presentation state only; a reload starts with chat open. */
let chatCollapsed = false;

/** How long a piece of invalid-action feedback stays on screen. It is a reply
 *  to one click, not a log entry: it fades so a run of mis-clicks never
 *  buries the party's messages. */
const FEEDBACK_MS = 3_500;
/** The transcript is bounded. A long floor used to append a node per event
 *  forever; the panel shows ~10 lines, so anything past this is unreachable
 *  anyway and only costs the page. */
const MAX_LINES = 150;

/** Chat panel: party lines, rare floor events, invalid-action feedback, input. */
export class ChatPanel {
  private panel: HTMLDivElement;
  private log: HTMLDivElement;
  private input: HTMLInputElement;
  private toggle: HTMLButtonElement;
  private unreadEl: HTMLSpanElement;
  private unread = 0;
  /** The feedback line currently showing, so a repeat bumps its count instead
   *  of appending another identical line. */
  private feedback: { el: HTMLDivElement; text: string; count: number; timer: number } | null = null;
  onSend: ((text: string) => void) | null = null;

  constructor(root: HTMLElement) {
    this.panel = document.createElement("div");
    this.panel.id = "chat-panel";
    this.panel.className = "panel";
    const head = document.createElement("div");
    head.className = "chat-head";
    const title = document.createElement("span");
    title.className = "chat-title";
    title.textContent = "Party chat";
    this.unreadEl = document.createElement("span");
    this.unreadEl.className = "chat-unread";
    this.toggle = document.createElement("button");
    this.toggle.type = "button";
    this.toggle.className = "chat-toggle";
    this.toggle.setAttribute("aria-controls", "chat-log chat-form");
    head.append(title, this.unreadEl, this.toggle);
    this.log = document.createElement("div");
    this.log.id = "chat-log";
    const form = document.createElement("form");
    form.id = "chat-form";
    this.input = document.createElement("input");
    this.input.placeholder = "Enter to chat · Enter to send";
    this.input.autocomplete = "off";
    form.appendChild(this.input);
    this.panel.append(head, this.log, form);
    root.appendChild(this.panel);

    this.toggle.addEventListener("click", () => this.setCollapsed(!this.panel.classList.contains("collapsed")));
    this.setCollapsed(chatCollapsed);

    form.addEventListener("submit", (ev) => {
      ev.preventDefault();
      const text = this.input.value.trim();
      if (text.length > 0) this.onSend?.(text);
      this.input.value = "";
      this.input.blur();
    });
    this.input.addEventListener("keydown", (ev) => {
      if (ev.key === "Escape") {
        ev.preventDefault();
        this.input.blur();
      }
    });
  }

  focusInput(): void {
    if (this.panel.classList.contains("collapsed")) this.setCollapsed(false);
    this.input.focus();
  }

  line(text: string, cls: string): void {
    const div = document.createElement("div");
    div.className = `line ${cls}`;
    div.textContent = text;
    this.append(div);
  }

  /** A floor event worth the party's attention (who found the key, who fell).
   *  What qualifies is decided in eventLines.ts, not here. */
  system(text: string): void {
    this.line(text, "system");
  }

  /** A rejected action: feedback for the one player who tried it, shown for a
   *  moment and then gone. Repeats bump a count instead of stacking lines. */
  invalid(text: string): void {
    const prev = this.feedback;
    if (prev && prev.text === text && prev.el.isConnected) {
      prev.count++;
      prev.el.textContent = `${text} (x${prev.count})`;
      window.clearTimeout(prev.timer);
      prev.timer = window.setTimeout(() => this.expireFeedback(prev), FEEDBACK_MS);
      return;
    }
    this.expireFeedback();
    const div = document.createElement("div");
    div.className = "line invalid feedback";
    div.textContent = text;
    const entry = { el: div, text, count: 1, timer: 0 };
    entry.timer = window.setTimeout(() => this.expireFeedback(entry), FEEDBACK_MS);
    this.feedback = entry;
    this.append(div);
  }

  party(name: string, text: string, color: string): void {
    const div = document.createElement("div");
    div.className = "line";
    const who = document.createElement("span");
    who.textContent = `${name}: `;
    who.style.color = color;
    const msg = document.createElement("span");
    msg.textContent = text;
    div.appendChild(who);
    div.appendChild(msg);
    this.append(div);
    // Only a teammate's message counts as unread: the badge answers "did
    // anyone say something to me", not "did anything happen".
    if (this.panel.classList.contains("collapsed")) {
      this.unread++;
      this.unreadEl.textContent = String(this.unread);
    }
  }

  private append(div: HTMLDivElement): void {
    this.log.appendChild(div);
    while (this.log.childElementCount > MAX_LINES) {
      const oldest = this.log.firstElementChild;
      if (!oldest) break;
      oldest.remove();
    }
    this.log.scrollTop = this.log.scrollHeight;
  }

  /** Fade the current feedback line out and drop it. Passing the entry that
   *  owns the timer means a superseded timer can never remove a newer line. */
  private expireFeedback(entry = this.feedback): void {
    if (!entry || entry !== this.feedback) return;
    this.feedback = null;
    window.clearTimeout(entry.timer);
    if (!entry.el.isConnected) return;
    entry.el.classList.add("fading");
    window.setTimeout(() => entry.el.remove(), 400);
  }

  private setCollapsed(collapsed: boolean): void {
    chatCollapsed = collapsed;
    this.panel.classList.toggle("collapsed", collapsed);
    this.toggle.textContent = collapsed ? "+" : "−";
    this.toggle.title = collapsed ? "Restore messages" : "Minimize messages";
    this.toggle.setAttribute("aria-label", this.toggle.title);
    this.toggle.setAttribute("aria-expanded", String(!collapsed));
    if (collapsed) {
      if (document.activeElement === this.input) this.toggle.focus();
      return;
    }
    this.unread = 0;
    this.unreadEl.textContent = "";
    this.log.scrollTop = this.log.scrollHeight;
  }
}
